export const SELF_SERVE_ONBOARDING_PAUSED_CODE = "self_serve_onboarding_paused";
const LEGACY_SELF_SERVE_ONBOARDING_DISABLED_MESSAGE = "Self-serve onboarding is not enabled.";

type SofaErrorResponseBody = {
  error?: {
    code?: string | null;
    message?: string | null;
  };
};

export class SofaApiError extends Error {
  readonly code?: string;
  readonly status: number;

  constructor(message: string, { code, status }: { code?: string; status: number }) {
    super(message);
    this.name = "SofaApiError";
    this.code = code;
    this.status = status;
  }
}

export async function buildSofaApiError(response: Response, fallback: string): Promise<Error> {
  try {
    const body = (await response.json()) as SofaErrorResponseBody;
    return buildSofaClientError(response.status, body, fallback);
  } catch {
    return new SofaApiError(fallback, { status: response.status });
  }
}

export function buildSofaClientError(status: number, payload: unknown, fallback: string): Error {
  const { code, message } = getSofaErrorDetails(status, payload, fallback);

  return new SofaApiError(message, {
    code,
    status,
  });
}

function getSofaErrorDetails(status: number, payload: unknown, fallback: string) {
  const body = payload as SofaErrorResponseBody | null | undefined;
  const code = body?.error?.code ?? undefined;
  const parsedMessage = body?.error?.message?.trim() || fallback;
  const message =
    status >= 400 && status < 500 ? parsedMessage
    : isSelfServeOnboardingPausedResponse(status, code, parsedMessage) ? parsedMessage
    : fallback;

  return {
    code,
    message,
  };
}

function isSelfServeOnboardingPausedResponse(
  status: number,
  code: string | undefined,
  message: string,
): boolean {
  return (
    status === 403 &&
    (code === SELF_SERVE_ONBOARDING_PAUSED_CODE ||
      message === LEGACY_SELF_SERVE_ONBOARDING_DISABLED_MESSAGE)
  );
}

export function isSelfServeOnboardingPausedError(error: unknown): boolean {
  if (!(error instanceof SofaApiError) || error.status !== 403) {
    return false;
  }

  return (
    error.code === SELF_SERVE_ONBOARDING_PAUSED_CODE ||
    error.message === LEGACY_SELF_SERVE_ONBOARDING_DISABLED_MESSAGE
  );
}
